import { CardContent, CardHeader, Typography, formatDate, Button, Card } from '@ui/components';
import { Success, Import, Report, Error, Info } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { useReportsPanel } from './use-reports-panel';

const ReportStatusText = ({
  isGenerating,
  isGenerated,
  isGeneratingTimeout,
  lastGeneratedAt,
}: {
  lastGeneratedAt: undefined | string;
  isGeneratingTimeout: boolean;
  isGenerating: boolean;
  isGenerated: boolean;
}) => {
  const { t } = useTranslation();
  if (isGeneratingTimeout) {
    return (
      <div className="flex gap-2 text-error">
        <Error />
        <Typography variant="labelMediumMedium">
          {t('FILES_REPORTS.REPORTS.REPORT_GENERATING_TIMEOUT')}
        </Typography>
      </div>
    );
  }
  if (isGenerating) {
    return (
      <div className="flex gap-2 text-weak">
        <Info />
        <Typography variant="labelMediumMedium">
          {t('FILES_REPORTS.REPORTS.REPORT_GENERATING_MESSAGE')}
        </Typography>
      </div>
    );
  }
  if (isGenerated) {
    return (
      <div className="flex gap-2 text-success">
        <Success />
        <Typography variant="labelMediumMedium">
          {t('FILES_REPORTS.REPORTS.REPORT_GENERATED_MESSAGE', {
            date: formatDate(lastGeneratedAt || '', '', DateTime.DATETIME_SHORT_WITH_SECONDS),
          })}
        </Typography>
      </div>
    );
  }
  return null;
};

export const ReportsPanel = () => {
  const { t } = useTranslation();
  const { allCostCentersSummaryReportController, allExpensesSummaryReportController } =
    useReportsPanel();

  return (
    <section className="flex flex-col gap-6">
      <Card className="min-h-72 p-2">
        <CardHeader>
          <Typography variant="labelLargeBold">
            {t('FILES_REPORTS.REPORTS.ALL_EXPENSES_SUMMARY_TITLE')}
          </Typography>
        </CardHeader>
        <CardContent className="flex gap-4 justify-between">
          <div className="w-2/5 space-y-4">
            <Typography variant="labelMediumMedium" className="text-weak">
              {t('FILES_REPORTS.REPORTS.ALL_EXPENSES_SUMMARY_DESCRIPTION')}
            </Typography>

            <div className="flex gap-4">
              <Button
                disabled={
                  !allExpensesSummaryReportController.isGenerated ||
                  allExpensesSummaryReportController.isReportDownloading
                }
                onClick={() => allExpensesSummaryReportController.downloadReport()}
                isLoading={allExpensesSummaryReportController.isReportDownloading}
                variant="tertiary"
                size="small"
              >
                <Import />
                {t('FILES_REPORTS.REPORTS.DOWNLOAD_REPORT')}
              </Button>

              <Button
                onClick={() => allExpensesSummaryReportController.generateReport()}
                isLoading={allExpensesSummaryReportController.isReportGenerating}
                disabled={allExpensesSummaryReportController.isReportGenerating}
                variant="secondary"
                size="small"
              >
                <Report />
                {t('FILES_REPORTS.REPORTS.GENERATE_REPORT')}
              </Button>
            </div>
            <ReportStatusText {...allExpensesSummaryReportController} />
          </div>

          <img src={`${import.meta.env.VITE_BASE_PATH}/images/chart_1.svg`} className="h-full" />
        </CardContent>
      </Card>

      <Card className="min-h-72 p-2">
        <CardHeader>
          <Typography variant="labelLargeBold">
            {t('FILES_REPORTS.REPORTS.ALL_COST_CENTERS_SUMMARY_TITLE')}
          </Typography>
        </CardHeader>
        <CardContent className="flex gap-4 justify-between">
          <div className="w-2/5 space-y-4">
            <Typography variant="labelMediumMedium" className="text-weak">
              {t('FILES_REPORTS.REPORTS.ALL_COST_CENTERS_SUMMARY_DESCRIPTION')}
            </Typography>

            <div className="flex gap-4">
              <Button
                disabled={
                  !allCostCentersSummaryReportController.isGenerated ||
                  allCostCentersSummaryReportController.isReportDownloading
                }
                onClick={() => allCostCentersSummaryReportController.downloadReport()}
                isLoading={allCostCentersSummaryReportController.isReportDownloading}
                variant="tertiary"
                size="small"
              >
                <Import />
                {t('FILES_REPORTS.REPORTS.DOWNLOAD_REPORT')}
              </Button>

              <Button
                onClick={() => allCostCentersSummaryReportController.generateReport()}
                isLoading={allCostCentersSummaryReportController.isReportGenerating}
                disabled={allCostCentersSummaryReportController.isReportGenerating}
                variant="secondary"
                size="small"
              >
                <Report />
                {t('FILES_REPORTS.REPORTS.GENERATE_REPORT')}
              </Button>
            </div>
            <ReportStatusText {...allCostCentersSummaryReportController} />
          </div>

          <img src={`${import.meta.env.VITE_BASE_PATH}/images/chart_2.svg`} className="h-full" />
        </CardContent>
      </Card>
    </section>
  );
};
